import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image, Svg, Path, G, Rect } from '@react-pdf/renderer';
import Html from 'react-pdf-html';

// Standard-Font (Roboto)
Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' }
    ]
});

const PRIORITY_COLORS = {
    critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Kritisch' },
    high: { bg: '#fff7ed', border: '#fdba74', text: '#c2410c', label: 'Hoch' },
    medium: { bg: '#fefce8', border: '#fde047', text: '#b45309', label: 'Mittel' },
    low: { bg: '#f0fdf4', border: '#86efac', text: '#15803d', label: 'Niedrig' }
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 45,
        paddingBottom: 65,
        paddingHorizontal: 40,
        fontFamily: 'Roboto',
        fontSize: 10,
        backgroundColor: '#ffffff'
    },
    coverPage: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 60,
        backgroundColor: '#f8fafc'
    },
    coverTitle: {
        fontSize: 32,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#0f172a',
        textAlign: 'center'
    },
    coverSubtitle: {
        fontSize: 18,
        marginBottom: 30,
        color: '#475569',
        textAlign: 'center'
    },
    coverInfoBox: {
        backgroundColor: '#ffffff',
        padding: 24,
        borderRadius: 8,
        width: '100%',
        borderLeft: '4pt solid #3b82f6',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    coverLabel: {
        fontSize: 10,
        color: '#64748b',
        marginBottom: 2
    },
    coverValue: {
        fontSize: 12,
        color: '#1e293b',
        marginBottom: 12,
        fontWeight: 'bold'
    },
    freeTextBox: {
        marginTop: 30,
        padding: 24,
        backgroundColor: '#ffffff',
        borderRadius: 8,
        width: '100%',
        borderLeft: '4pt solid #10b981',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 2,
        borderBottomColor: '#3b82f6',
        paddingBottom: 10,
        marginBottom: 20
    },
    headerText: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#3b82f6',
        textTransform: 'uppercase'
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 1,
        borderTopColor: '#cbd5e1',
        paddingTop: 10
    },
    footerText: {
        fontSize: 8,
        color: '#94a3b8'
    },
    entryContainer: {
        marginBottom: 25,
        borderRadius: 6,
        breakInside: 'avoid',
        overflow: 'hidden'
    },
    entryHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0'
    },
    entryHeaderLeft: {
        flex: 1
    },
    entryHeaderRight: {
        alignItems: 'flex-end',
        width: 200
    },
    entryDateTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#0f172a',
        marginBottom: 4
    },
    entryTitle: {
        fontSize: 10,
        color: '#334155',
        fontWeight: 'bold'
    },
    badge: {
        paddingHorizontal: 6,
        paddingVertical: 3,
        borderRadius: 4,
        fontSize: 8,
        fontWeight: 'bold',
        marginBottom: 4,
        borderWidth: 1
    },
    weatherBox: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        marginTop: 4
    },
    weatherText: {
        fontSize: 8,
        color: '#64748b'
    },
    entryBody: {
        padding: 12,
        backgroundColor: '#ffffff'
    },
    authorText: {
        fontSize: 8,
        color: '#94a3b8',
        marginBottom: 8
    },
    textContent: {
        fontSize: 10,
        lineHeight: 1.6,
        color: '#334155'
    },
    imagesGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        padding: 12,
        paddingTop: 0
    },
    imageWrapper: {
        width: '31%', // roughly 3 per row
        aspectRatio: '1',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRadius: 4,
        overflow: 'hidden'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    }
});

const formatWeather = (entry) => {
    let parts = [];
    if (entry.weather) parts.push(entry.weather);
    const data = entry.weather_data;
    if (data && typeof data === 'object') {
        if (data.temp_min !== undefined || data.temp_max !== undefined) {
            parts.push(`${data.temp_min || '-'}°C bis ${data.temp_max || '-'}°C`);
        }
        if (data.precipitation !== undefined) {
            parts.push(`${data.precipitation} mm`);
        }
    }
    return parts.join(' | ');
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE');
};

const htmlStyles = StyleSheet.create({
    html: { fontSize: 10, color: '#334155' },
    p: { fontSize: 10, lineHeight: 1.2, margin: 0, padding: 0 },
    h1: { fontSize: 13, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    h2: { fontSize: 11, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    h3: { fontSize: 10, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    ul: { margin: 0, paddingLeft: 10 },
    ol: { margin: 0, paddingLeft: 10 },
    li: { fontSize: 10, lineHeight: 1.2, margin: 0, padding: 0 },
});

const PdfDocument = ({ project, entries, filterText, reportTitle, coverOptions, teamMembers, creatorName, freeTextHtml, freeTextPosition = 'between_team_entries' }) => {
    const displayTitle = reportTitle && reportTitle.trim() !== '' ? reportTitle : 'Bautagebuch';

    const renderFreeText = () => {
        if (!freeTextHtml) return null;
        const plainText = freeTextHtml.replace(/<[^>]*>/g, '').trim();
        if (!plainText) return null;

        return (
            <View style={styles.freeTextBox} wrap={false}>
                <Html stylesheet={htmlStyles}>{freeTextHtml}</Html>
            </View>
        );
    };

    return (
        <Document>
            {/* Cover Page */}
            <Page size="A4" style={styles.coverPage}>
                <View style={{ marginBottom: 40, alignItems: 'center' }}>
                    <Text style={styles.coverTitle}>{displayTitle}</Text>
                    <Text style={styles.coverSubtitle}>{project?.name}</Text>
                </View>

                <View style={styles.coverInfoBox}>
                    <Text style={styles.coverLabel}>Projektnummer</Text>
                    <Text style={styles.coverValue}>{project?.project_number || '-'}</Text>

                    {coverOptions?.showLocation && (
                        <>
                            <Text style={styles.coverLabel}>Standort</Text>
                            <Text style={styles.coverValue}>{project?.address || project?.city ? `${project?.address || ''} ${project?.city || ''}`.trim() : '-'}</Text>
                        </>
                    )}

                    {coverOptions?.showStatus && (
                        <>
                            <Text style={styles.coverLabel}>Projektstatus</Text>
                            <Text style={styles.coverValue}>
                                {project?.status === 'active' || project?.status === 'Active' ? 'Aktiv' :
                                    project?.status === 'completed' || project?.status === 'Completed' ? 'Abgeschlossen' :
                                        project?.status === 'archived' || project?.status === 'Archived' ? 'Archiviert' :
                                            project?.status || '-'}
                            </Text>
                        </>
                    )}

                    <Text style={styles.coverLabel}>Exportzeitraum</Text>
                    <Text style={styles.coverValue}>{filterText}</Text>

                    <Text style={styles.coverLabel}>Anzahl Einträge</Text>
                    <Text style={styles.coverValue}>{entries.length}</Text>

                    <Text style={styles.coverLabel}>Ausstellungsdatum</Text>
                    <Text style={styles.coverValue}>{new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</Text>

                    {creatorName && (
                        <>
                            <Text style={styles.coverLabel}>Ausgestellt von</Text>
                            <Text style={styles.coverValue}>{creatorName}</Text>
                        </>
                    )}
                </View>
            </Page>

            {/* Position: Between Cover and Team (New Page) */}
            {freeTextPosition === 'between_cover_team' && freeTextHtml && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.header}>
                        <Text style={styles.headerText}>{project?.project_number ? `Projekt ${project.project_number}` : project?.name}</Text>
                        <Text style={styles.headerText}>{displayTitle}</Text>
                    </View>
                    <View style={{ marginTop: 20 }}>
                        {renderFreeText()}
                    </View>
                </Page>
            )}

            {/* Team List Page */}
            {coverOptions?.showTeam && teamMembers && teamMembers.length > 0 && (
                <Page size="A4" style={styles.page} wrap>
                    <View style={styles.header} fixed>
                        <Text style={styles.headerText}>{project?.project_number ? `Projekt ${project.project_number}` : project?.name}</Text>
                        <Text style={styles.headerText}>Teamliste</Text>
                    </View>

                    {/* Active Team */}
                    {(() => {
                        const activeTeam = teamMembers.filter(u => u.project_role !== 'blacklisted' && u.permissions?.journal?.view !== false);
                        if (activeTeam.length > 0) {
                            return (
                                <>
                                    <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, color: '#0f172a' }}>Projektteam</Text>
                                    <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4, marginBottom: 25 }}>
                                        <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', padding: 8, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }}>
                                            <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 10 }}>Name</Text>
                                            <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 10 }}>Rolle</Text>
                                        </View>
                                        {activeTeam.map((member, i) => (
                                            <View key={i} style={{ flexDirection: 'row', padding: 8, borderBottomWidth: i < activeTeam.length - 1 ? 1 : 0, borderBottomColor: '#e2e8f0' }}>
                                                <Text style={{ flex: 1, fontSize: 10 }}>{member.name || '-'}</Text>
                                                <Text style={{ flex: 1, fontSize: 10 }}>{(member.project_role === 'project_admin' ? '' : member.project_role) || '-'}</Text>
                                            </View>
                                        ))}
                                    </View>
                                </>
                            );
                        }
                        return null;
                    })()}

                    {/* Blacklist */}
                    {coverOptions?.showBlacklist && (() => {
                        const blacklisted = teamMembers.filter(u => u.project_role === 'blacklisted' || u.permissions?.journal?.view === false);
                        if (blacklisted.length > 0) {
                            return (
                                <>
                                    <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, color: '#0f172a' }}>Blacklist</Text>
                                    <View style={{ borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4 }}>
                                        <View style={{ flexDirection: 'row', backgroundColor: '#f8fafc', padding: 8, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }}>
                                            <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 10 }}>Name</Text>
                                        </View>
                                        {blacklisted.map((member, i) => (
                                            <View key={i} style={{ flexDirection: 'row', padding: 8, borderBottomWidth: i < blacklisted.length - 1 ? 1 : 0, borderBottomColor: '#e2e8f0' }}>
                                                <Text style={{ flex: 1, fontSize: 10 }}>{member.name || '-'}</Text>
                                            </View>
                                        ))}
                                    </View>
                                </>
                            );
                        }
                        return null;
                    })()}

                    {/* Position: Between Team and Entries (Inline - same page if space) */}
                    {freeTextPosition === 'between_team_entries' && (
                        <View style={{ marginTop: 20 }}>
                            {renderFreeText()}
                        </View>
                    )}

                    <View style={styles.footer} fixed>
                        <Text style={styles.footerText}>
                            {project?.project_number ? `${project.project_number} - ` : ''}{project?.name}
                            {creatorName ? ` • Exportiert von ${creatorName}` : ''}
                            • Ausgabedatum: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Text>
                        <Text style={styles.footerText} render={({ pageNumber, totalPages }) => (
                            `Seite ${pageNumber} von ${totalPages}`
                        )} />
                    </View>
                </Page>
            )}

            {/* Edge Case: No Team shown, but Freitext "between_team_entries" */}
            {!coverOptions?.showTeam && freeTextPosition === 'between_team_entries' && freeTextHtml && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.header}>
                        <Text style={styles.headerText}>{project?.project_number ? `Projekt ${project.project_number}` : project?.name}</Text>
                        <Text style={styles.headerText}>{displayTitle}</Text>
                    </View>
                    <View style={{ marginTop: 20 }}>
                        {renderFreeText()}
                    </View>

                    <View style={styles.footer} fixed>
                        <Text style={styles.footerText}>
                            {project?.project_number ? `${project.project_number} - ` : ''}{project?.name}
                            {creatorName ? ` • Exportiert von ${creatorName}` : ''}
                            • Ausgabedatum: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Text>
                        <Text style={styles.footerText} render={({ pageNumber, totalPages }) => (
                            `Seite ${pageNumber} von ${totalPages}`
                        )} />
                    </View>
                </Page>
            )}

            {/* Content Pages */}
            <Page size="A4" style={styles.page} wrap>

                {/* Repeating Header */}
                <View style={styles.header} fixed>
                    <Text style={styles.headerText}>{project?.project_number ? `Projekt ${project.project_number}` : project?.name}</Text>
                    <Text style={styles.headerText}>{displayTitle}</Text>
                </View>

                {/* Entries */}
                {entries.map((entry, index) => {
                    const prio = PRIORITY_COLORS[entry.problem_priority] || PRIORITY_COLORS.low;

                    return (
                        <View key={entry.id || index} style={[styles.entryContainer, { borderWidth: 1, borderColor: prio.border }]} wrap={false}>
                            {/* Header Row */}
                            <View style={[styles.entryHeaderRow, { backgroundColor: prio.bg }]}>
                                <View style={styles.entryHeaderLeft}>
                                    <Text style={styles.entryDateTitle}>
                                        {formatDate(entry.date)} {entry.time ? `• ${entry.time} Uhr` : ''}
                                    </Text>
                                    {entry.title && <Text style={styles.entryTitle}>{entry.title}</Text>}
                                </View>

                                <View style={styles.entryHeaderRight}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        {entry.attachments?.some(a => a.file_type === 'audio') && (
                                            <View style={{ backgroundColor: '#f1f5f9', paddingHorizontal: 4, paddingVertical: 2, borderRadius: 4, borderWidth: 1, borderColor: '#cbd5e1', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                                <Svg width="10" height="10" viewBox="0 0 24 24">
                                                    <G fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <Path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                                        <Path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                                        <Path d="M12 19v4" />
                                                        <Path d="M8 23h8" />
                                                    </G>
                                                </Svg>
                                                <Text style={{ fontSize: 9, color: '#475569' }}>Audio</Text>
                                            </View>
                                        )}
                                        <View style={[styles.badge, { backgroundColor: prio.bg, borderColor: prio.border, color: prio.text, marginBottom: 0, marginTop: 0 }]}>
                                            <Text>{prio.label}</Text>
                                        </View>
                                    </View>
                                    <Text style={styles.weatherText}>{formatWeather(entry)}</Text>
                                </View>
                            </View>

                            {/* Content Row */}
                            <View style={styles.entryBody}>
                                <Text style={styles.authorText}>Erfasst von {entry.author_name || 'Unbekannt'}</Text>
                                <Text style={styles.textContent}>{entry.content}</Text>
                            </View>

                            {/* Images Row */}
                            {entry.selectedAttachments && entry.selectedAttachments.length > 0 && (
                                <View style={styles.imagesGrid}>
                                    {entry.selectedAttachments.map((att, i) => (
                                        att.public_url ? (
                                            <View key={i} style={styles.imageWrapper}>
                                                <Image src={att.public_url} style={styles.image} />
                                            </View>
                                        ) : null
                                    ))}
                                </View>
                            )}
                        </View>
                    );
                })}

                {/* Repeating Footer */}
                <View style={styles.footer} fixed>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.footerText}>
                            {project?.project_number ? `${project.project_number} - ` : ''}{project?.name}
                            {creatorName ? ` • Exportiert von ${creatorName}` : ''}
                        </Text>
                        {project?.address && <Text style={[styles.footerText, { marginTop: 2 }]}>{project.address}, {project.city}</Text>}
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.footerText}>Ausgabedatum: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</Text>
                        <Text style={[styles.footerText, { marginTop: 2 }]} render={({ pageNumber, totalPages }) => (
                            `Seite ${pageNumber} von ${totalPages}`
                        )} />
                    </View>
                </View>

                {/* Position: After Entries */}
                {freeTextPosition === 'after_entries' && renderFreeText()}
            </Page>
        </Document>
    );
};

export default PdfDocument;
